import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { appVersionConfigApi } from '../services/api';
import PaymentsToggles from './PaymentsToggles';

const PLATFORM_LABELS = {
  ios: 'iOS',
  android: 'Android',
  web_admin: 'Admin Panel (web)',
  web_provider: 'Provider Dashboard (web)',
};

const FREQUENCIES = ['SESSION', 'DAILY', 'ALWAYS'];

const emptyForm = (row) => ({
  latestVersion: row.latestVersion || '',
  minSupportedVersion: row.minSupportedVersion || '',
  recommendedVersion: row.recommendedVersion || '',
  panicMode: !!row.panicMode,
  recommendFrequency: row.recommendFrequency || 'SESSION',
  updateMessageEn: row.updateMessageEn || '',
  updateMessageHe: row.updateMessageHe || '',
  updateMessageEs: row.updateMessageEs || '',
  maintenanceMessageEn: row.maintenanceMessageEn || '',
  maintenanceMessageHe: row.maintenanceMessageHe || '',
  maintenanceMessageEs: row.maintenanceMessageEs || '',
  storeUrl: row.storeUrl || '',
  webBuildId: row.webBuildId || '',
});

function PlatformCard({ row, onSaved }) {
  const [form, setForm] = useState(emptyForm(row));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        latestVersion: form.latestVersion.trim(),
        minSupportedVersion: form.minSupportedVersion.trim(),
        recommendedVersion: form.recommendedVersion.trim(),
        panicMode: !!form.panicMode,
        recommendFrequency: form.recommendFrequency,
        updateMessageEn: form.updateMessageEn || null,
        updateMessageHe: form.updateMessageHe || null,
        updateMessageEs: form.updateMessageEs || null,
        maintenanceMessageEn: form.maintenanceMessageEn || null,
        maintenanceMessageHe: form.maintenanceMessageHe || null,
        maintenanceMessageEs: form.maintenanceMessageEs || null,
        storeUrl: form.storeUrl || null,
        webBuildId: form.webBuildId || null,
      };
      const saved = await appVersionConfigApi.update(row.platform, payload);
      setSuccess('Saved');
      onSaved && onSaved(saved);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const isWeb = row.platform.startsWith('web_');

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">{PLATFORM_LABELS[row.platform] || row.platform}</Typography>
          <Typography variant="caption" color="text.secondary">
            Updated {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
            {row.updatedBy ? ` by ${row.updatedBy}` : ''}
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Latest version"
              value={form.latestVersion}
              onChange={set('latestVersion')}
              placeholder="1.0.0"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Min supported version"
              value={form.minSupportedVersion}
              onChange={set('minSupportedVersion')}
              helperText="Below this → force upgrade"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Recommended version"
              value={form.recommendedVersion}
              onChange={set('recommendedVersion')}
              helperText="Below this → soft nudge"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.panicMode}
                  onChange={set('panicMode')}
                  color="error"
                />
              }
              label="Panic mode (maintenance)"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Recommend frequency"
              value={form.recommendFrequency}
              onChange={set('recommendFrequency')}
            >
              {FREQUENCIES.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={isWeb ? 'Web build id' : 'Store URL'}
              value={isWeb ? form.webBuildId : form.storeUrl}
              onChange={isWeb ? set('webBuildId') : set('storeUrl')}
              helperText={isWeb ? 'Bumped by CI on each deploy' : 'App Store / Play Store link'}
            />
          </Grid>

          {!isWeb && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Web build id (unused for mobile)"
                value={form.webBuildId}
                onChange={set('webBuildId')}
                disabled
              />
            </Grid>
          )}
          {isWeb && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Store URL (unused for web)"
                value={form.storeUrl}
                onChange={set('storeUrl')}
                disabled
              />
            </Grid>
          )}

          <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

          <Grid item xs={12}>
            <Typography variant="overline" color="text.secondary">
              Upgrade screen / banner message
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Upgrade message (EN)"
              value={form.updateMessageEn}
              onChange={set('updateMessageEn')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Upgrade message (HE)"
              value={form.updateMessageHe}
              onChange={set('updateMessageHe')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Upgrade message (ES)"
              value={form.updateMessageEs}
              onChange={set('updateMessageEs')}
            />
          </Grid>

          <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

          <Grid item xs={12}>
            <Typography variant="overline" color="text.secondary">
              Maintenance screen message (shown when panic mode is on)
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Maintenance message (EN)"
              value={form.maintenanceMessageEn}
              onChange={set('maintenanceMessageEn')}
              placeholder="App is under maintenance. We'll be back soon."
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Maintenance message (HE)"
              value={form.maintenanceMessageHe}
              onChange={set('maintenanceMessageHe')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Maintenance message (ES)"
              value={form.maintenanceMessageEs}
              onChange={set('maintenanceMessageEs')}
            />
          </Grid>
        </Grid>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

        <Box mt={2} textAlign="right">
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={saving}
            onClick={handleSave}
          >
            Save
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

const AppVersionConfig = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await appVersionConfigApi.listAll();
      setRows(data || []);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSaved = (saved) => {
    setRows((prev) => prev.map((r) => (r.platform === saved.platform ? saved : r)));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h5">App Version Compatibility</Typography>
        <Typography variant="body2" color="text.secondary">
          Per-platform settings consumed by mobile clients on startup and by web dashboards
          to trigger a refresh after deploy. Below <strong>min_supported_version</strong> →
          force upgrade screen. Below <strong>recommended_version</strong> → dismissible banner.
        </Typography>
      </Paper>

      <PaymentsToggles />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={3}>
        {rows.map((row) => (
          <PlatformCard key={row.platform} row={row} onSaved={handleSaved} />
        ))}
      </Stack>
    </Box>
  );
};

export default AppVersionConfig;
